// @ts-check
import React, { useEffect, useState } from 'react';
import { fromApiBoolean } from '@liquidweb-dev/club-sauce/types/boolean';
import useAssetList from 'modules/queries/asset/useList';
import type { DialogProps as UndercurrentDialogProps } from '@liquidweb-dev/undercurrent/Dialog';
import Stack from '@liquidweb-dev/undercurrent/Stack';
import LoaderCircleIcon from '@liquidweb-dev/undercurrent/icons/animated/LoaderCircle';
import useAssetCancellationRequestDetails from 'modules/queries/asset/cancellation/request/useRequestDetails';
import {
	ASSET_CATEGORIES,
	CANCELLATION_STAGE,
	INITIAL_FORM_VALUES,
} from './constants';
import CancelService from './CancelService';
import FastCancellation from './FastCancellation';
import RequestAssistance from './RequestAssistance';
import SubmittedRequest from './SubmittedRequest';
import LastServerCleanup from './LastServerCleanup';
import CancellationLoader from './CancellationLoader';
import Wrapper from './Wrapper';

type DialogDestroyServerProps = Omit<UndercurrentDialogProps, 'onClose'> & {
	uniqId: string;
	onClose: () => void;
};

const DialogDestroyServer: React.FC<DialogDestroyServerProps> = ({
	uniqId,
	onClose,
	...dialogProps
}) => {
	const { open } = dialogProps;

	const [currentStage, setCurrentStage] = useState('');
	const [formValues, setFormValues] = useState(INITIAL_FORM_VALUES);

	const { isLoading, data: cancellationCheck } =
		useAssetCancellationRequestDetails(
			{ uniq_id: uniqId },
			{ enabled: Boolean(open) },
		);
	const { data: assetList } = useAssetList(
		{
			category: ASSET_CATEGORIES,
			page_size: 9999,
		},
		{ enabled: Boolean(open) },
	);

	const hasOpenCancellationCase = Boolean(
		cancellationCheck?.cancellation_ticket,
	);
	const isAssisted = fromApiBoolean(cancellationCheck?.offer_assistance);
	const isLastAsset = assetList?.item_count === 1;

	useEffect(() => {
		// If the dialog is open and not loading, determine the initial stage
		// based on whether there is an open cancellation case.
		// If there is an open cancellation case, set the stage to SUBMITTED_REQUEST,
		// otherwise set it to CANCEL_SERVICE.
		// This ensures that the user sees the correct stage when they open the dialog.
		// If the dialog is not open, we reset the stage to an empty string.
		// This is important to avoid showing the dialog in an inconsistent state.
		if (open && !isLoading) {
			setCurrentStage(
				hasOpenCancellationCase
					? CANCELLATION_STAGE.SUBMITTED_REQUEST
					: CANCELLATION_STAGE.CANCEL_SERVICE,
			);
		}
	}, [isLoading, hasOpenCancellationCase, open]);

	useEffect(() => {
		if (cancellationCheck?.offer_assistance) {
			setFormValues({
				...INITIAL_FORM_VALUES,
				cancellation_reason: cancellationCheck.cancellation_reason || '',
				additional_info: cancellationCheck.additional_info || '',
			});
		}
	}, [cancellationCheck, open]);

	const handleClose = () => {
		setFormValues(INITIAL_FORM_VALUES);
		setCurrentStage('');
		onClose();
	};

	const handleServiceSubmit = () => {
		setCurrentStage(() => {
			if (!isAssisted) {
				return isLastAsset
					? CANCELLATION_STAGE.LAST_SERVER_CLEANUP
					: CANCELLATION_STAGE.FAST_CANCELLATION;
			}

			if (hasOpenCancellationCase) {
				return CANCELLATION_STAGE.SUBMITTED_REQUEST;
			}
			return CANCELLATION_STAGE.REQUEST_ASSISTANCE;
		});
	};

	const handleFastCancellationSubmit = () => {
		setFormValues(INITIAL_FORM_VALUES);
		onClose();
	};

	const handleLastServerCleanupSubmit = () => {
		setCurrentStage(CANCELLATION_STAGE.FAST_CANCELLATION);
	};

	const handleLastServerCleanupPrevious = () => {
		setCurrentStage(CANCELLATION_STAGE.CANCEL_SERVICE);
	};

	const handleFastCancellationPrevious = () => {
		setCurrentStage(
			isLastAsset
				? CANCELLATION_STAGE.LAST_SERVER_CLEANUP
				: CANCELLATION_STAGE.CANCEL_SERVICE,
		);
	};

	const handleRequestAssistancePrevious = () => {
		setCurrentStage(CANCELLATION_STAGE.CANCEL_SERVICE);
	};

	const handleRequestAssistanceSubmit = () => {
		setCurrentStage(CANCELLATION_STAGE.SUBMITTED_REQUEST);
	};

	const handleCancelWithoutAssistance = () => {
		if (isLastAsset) {
			setCurrentStage(CANCELLATION_STAGE.LAST_SERVER_CLEANUP);
			return;
		}
		setCurrentStage(CANCELLATION_STAGE.FAST_CANCELLATION);
	};

	if (open && isLoading) {
		return (
			<Wrapper
				currentStage={currentStage}
				onClose={handleClose}
				{...dialogProps}
			>
				<Stack width="100%">
					<CancellationLoader
						title="One moment please..."
						icon={<LoaderCircleIcon />}
					/>
				</Stack>
			</Wrapper>
		);
	}

	if (currentStage === CANCELLATION_STAGE.CANCEL_SERVICE) {
		return (
			<Wrapper
				currentStage={currentStage}
				onClose={handleClose}
				{...dialogProps}
			>
				<CancelService
					uniqId={uniqId}
					formValues={formValues}
					onFormValuesChange={setFormValues}
					onSubmit={handleServiceSubmit}
				/>
			</Wrapper>
		);
	}

	if (currentStage === CANCELLATION_STAGE.LAST_SERVER_CLEANUP) {
		return (
			<Wrapper
				currentStage={currentStage}
				onPrevious={handleLastServerCleanupPrevious}
				onClose={handleClose}
				{...dialogProps}
			>
				<LastServerCleanup
					formValues={formValues}
					onFormValuesChange={setFormValues}
					onSubmit={handleLastServerCleanupSubmit}
				/>
			</Wrapper>
		);
	}

	if (currentStage === CANCELLATION_STAGE.FAST_CANCELLATION) {
		return (
			<Wrapper
				currentStage={currentStage}
				onPrevious={handleFastCancellationPrevious}
				onClose={handleClose}
				{...dialogProps}
			>
				<FastCancellation
					uniqId={uniqId}
					formValues={formValues}
					onFormValuesChange={setFormValues}
					onSubmit={handleFastCancellationSubmit}
				/>
			</Wrapper>
		);
	}

	if (currentStage === CANCELLATION_STAGE.REQUEST_ASSISTANCE) {
		return (
			<Wrapper
				currentStage={currentStage}
				onPrevious={handleRequestAssistancePrevious}
				onClose={handleClose}
				{...dialogProps}
			>
				<RequestAssistance
					handleRequestAssistanceSubmit={handleRequestAssistanceSubmit}
					onCancelWithoutAssistance={handleCancelWithoutAssistance}
				/>
			</Wrapper>
		);
	}

	if (currentStage === CANCELLATION_STAGE.SUBMITTED_REQUEST) {
		return (
			<Wrapper
				currentStage={currentStage}
				onClose={handleClose}
				{...dialogProps}
			>
				<SubmittedRequest
					uniqId={uniqId}
					ticketId={cancellationCheck?.cancellation_ticket}
					formValues={formValues}
					onCancelWithoutAssistance={handleCancelWithoutAssistance}
				/>
			</Wrapper>
		);
	}

	return null;
};

export default DialogDestroyServer;
